/**
 * Wiring the offline queue into React.
 *
 * `useOutboxStatus` gives a screen the two numbers a user needs: how many of
 * their writes are still waiting, and how many the server refused. The second
 * one matters most — a rejected write that disappears from every indicator is
 * a mark the teacher believes they recorded.
 *
 * `useDrainOnReconnect` is the pump. It fires on `online`, on tab focus, and on
 * a slow timer. Three triggers because none is reliable on its own: `online`
 * lies behind a captive portal, focus never fires on a wall-mounted tablet, and
 * a timer alone leaves work sitting for a minute after the signal returns.
 * The drain is single-flight, so overlapping triggers cost nothing.
 */
import { useCallback, useEffect, useState } from "react";
import { drainOutbox, onOutbox, outboxCounts, recoverInFlight } from "./outbox";

/** Slow on purpose: the event triggers do the real work, this is the backstop. */
const POLL_MS = 60_000;

/**
 * Retries after a drain that hit the network, in ms.
 *
 * The `online` event is optimistic: it fires when the interface comes up, which
 * on a phone reattaching to a tower is seconds before anything actually
 * routes. Observed exactly that — reconnect, drain, every row fails with a
 * network error, and the queue then sat untouched for a full minute while the
 * screen said "en attente de réseau" on a working connection.
 *
 * Bounded and increasing, so a genuinely dead link costs four attempts rather
 * than a spin.
 */
const RETRY_MS = [2_000, 5_000, 15_000];

export function useOutboxStatus(entity?: string) {
  const [counts, setCounts] = useState({ pending: 0, failed: 0 });

  const refresh = useCallback(() => {
    void outboxCounts(entity).then(setCounts);
  }, [entity]);

  useEffect(() => {
    refresh();
    // Any queue movement changes the numbers, so listen rather than poll.
    return onOutbox(refresh);
  }, [refresh]);

  return { ...counts, refresh };
}

/**
 * Drain when connectivity comes back. Mount once, high in the tree.
 *
 * Also recovers rows a killed tab left in flight — that has to happen at boot,
 * before anything else looks at the queue.
 */
export function useDrainOnReconnect() {
  useEffect(() => {
    let cancelled = false;
    const retries: number[] = [];

    const clearRetries = () => {
      while (retries.length) window.clearTimeout(retries.pop()!);
    };

    const pump = (attempt = 0) => {
      if (cancelled) return;
      // navigator.onLine is trusted when it says OFFLINE and not when it says
      // online (a captive portal reports true). So we only use it to skip a
      // pointless attempt, never to decide we are connected.
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      void drainOutbox().then(async ({ acked }) => {
        if (cancelled || acked > 0) return;
        // Nothing went through. If work is still queued, the link is probably
        // not up yet — try again shortly rather than waiting for the next tick.
        const { pending } = await outboxCounts();
        const delay = RETRY_MS[attempt];
        if (pending > 0 && delay !== undefined && !cancelled) {
          retries.push(window.setTimeout(() => pump(attempt + 1), delay));
        }
      });
    };

    const restart = () => { clearRetries(); pump(); };

    void recoverInFlight().then(() => pump());

    window.addEventListener("online", restart);
    document.addEventListener("visibilitychange", restart);
    const timer = window.setInterval(() => pump(), POLL_MS);

    return () => {
      cancelled = true;
      clearRetries();
      window.removeEventListener("online", restart);
      document.removeEventListener("visibilitychange", restart);
      window.clearInterval(timer);
    };
  }, []);
}
